import { ButtonEvent } from "appworks/graphics/elements/button-element";
import { PIXIElement } from "appworks/graphics/pixi/group";
import { gameState } from "appworks/model/game-state";
import { Services } from "appworks/services/services";
import { TranslationsService } from "appworks/services/translations/translations-service";
import { Contract } from "appworks/utils/contracts/contract";
import { SlingoLadderResult } from "slingo/model/results/slingo-ladder-result";
import { SlingoPurchaseSpinOrCollectState } from "slingo/states/slingo-purchase-spin-or-collect-state";
import { SlingoPotentialWin } from "slingo/util/slingo-get-potential-wins";

export class FowlPurchaseSpinOrCollectState extends SlingoPurchaseSpinOrCollectState {
    protected getPotentialWinDisplayElements(): PIXIElement[] {
        const elements = super.getPotentialWinDisplayElements();

        elements.push(this.layer.getShape("shadow"));
        elements.push(this.layer.getText("pot_text"));
        elements.push(this.layer.getText("you_could_win"));

        for (let i = 0; i <= 12; i++) {
            const potentialPrize = this.layer.getSprite(`pot_${i}`);
            if (potentialPrize) {
                elements.push(potentialPrize);
            }
        }

        return elements;
    }

    protected hideElementsIfNoPotentialWins(): void {
        this.hidePrizeGraphics();
        this.showCurrentPrizeGraphicOnCollectButton();
        super.hideElementsIfNoPotentialWins();
    }

    protected showPotentialWin(win: SlingoPotentialWin): void {
        super.showPotentialWin(win);

        this.hidePrizeGraphics();
        this.showCurrentPrizeGraphicOnCollectButton();

        // Show prize graphic
        const potPrize = this.layer.get("pot_" + win.lines);
        if (potPrize) {
            potPrize.visible = true;
        }

        // Set potential prize text
        const potText = this.layer.getText("pot_text");
        potText.text = this.getPrizeTextForLevel(win.lines);
    }

    protected showCurrentPrizeGraphicOnCollectButton() {
        const result = gameState.getCurrentGame().getLatestResultOfType(SlingoLadderResult);
        const currentPrize = this.layer.getSprite(`col_${result.total}`);
        if (currentPrize) {
            currentPrize.visible = true;
        }

        const prizeText = this.layer.getText("col_text");
        prizeText.text = this.getPrizeTextForLevel(result.total);
        if (result.total === 6) {
            // only on the button, not in the potential win frame
            prizeText.text = Services.get(TranslationsService).get("bonus_caps");
        }
    }

    protected hidePrizeGraphics() {
        // Hide all prize graphics
        for (let i = 0; i <= 12; i++) {
            const potentialPrize = this.layer.getSprite(`pot_${i}`);
            if (potentialPrize) {
                potentialPrize.visible = false;
            }

            const collectPrize = this.layer.getSprite(`col_${i}`);
            if (collectPrize) {
                collectPrize.visible = false;
            }
        }
    }

    protected enableButtons(): Contract<any> {
        this.enableFPSpecificButtons(true);
        return super.enableButtons();
    }

    protected disableButtons(): Contract<any> {
        this.enableFPSpecificButtons(false);
        return super.disableButtons();
    }

    protected enableFPSpecificButtons(enable: boolean) {
        const endButton = this.layer.getButton("end_game");
        endButton?.setEnabled(enable);

        const elements = ["col_text"];
        for (let i = 1; i <= 12; i++) {
            elements.push("col_" + i);
        }

        elements.forEach(elName => {
            const element = this.layer.get(elName);
            if (element) {
                element.alpha = enable ? 1 : 0.5;
            }
        });
    }

    protected setCollectButton() {
        const result = gameState.getCurrentGame().getLatestResultOfType(SlingoLadderResult);

        const endButton = this.layer.getButton("end_game");
        const collectButton = this.layer.getButton("collect");

        endButton.on(ButtonEvent.CLICK, () => this.collect());
        collectButton.on(ButtonEvent.CLICK, () => this.collect());

        endButton.visible = result.total < 4;
        collectButton.visible = result.total >= 4;
    }

    protected getPrizeTextForLevel(lvl: number): string {
        switch (lvl) {
            case 4:
            case 5:
                return "1 " + Services.get(TranslationsService).get("egg_pick_caps");

            case 7:
            case 9:
                return Services.get(TranslationsService)
                    .get("x_egg_picks", { value: 2 })
                    .toLocaleUpperCase();

            case 8:
                return Services.get(TranslationsService)
                    .get("plus_x_wilds", { value: 120 })
                    .toLocaleUpperCase();

            case 10:
                return Services.get(TranslationsService)
                    .get("plus_x_wilds", { value: 240 })
                    .toLocaleUpperCase();

            case 12:
                return Services.get(TranslationsService)
                    .get("x_egg_picks", { value: 3 })
                    .toLocaleUpperCase();

            default:
                return "";
        }
    }

    protected getMinWinLevel(): number {
        return 4;
    }
}
